import { Undef } from 'types/UtilityTypes';
import { formatCurrency } from 'utils/formatCurrency';

import { LTLRateMetadata } from '../LTLRateCardMetadata';

interface TaiRateMetadataTooltipProps {
  rateMetadata: LTLRateMetadata;
}

type AccessorialPrice = {
  accessorialName: string;
  buy: number;
  sell: number;
};

export function RateMetadataTooltipContent({
  rateMetadata,
}: TaiRateMetadataTooltipProps) {
  const markupAmount = rateMetadata.margins?.[0]?.markupAmount ?? 0;
  const buyRateFreightCharges = rateMetadata.buyRateFreightCharges ?? 0;
  const sellRateFreightCharges = rateMetadata.sellRateFreightCharges ?? 0;
  const buyFuelSurcharge = rateMetadata.buyFuelSurcharge ?? 0;
  const sellFuelSurcharge = rateMetadata.sellFuelSurcharge ?? 0;
  const buyRateTotal = rateMetadata.buyRateTotal ?? 0;
  const sellRateTotal = rateMetadata.sellRateTotal ?? 0;

  let fuelRateMargin = 0;
  if (sellFuelSurcharge > 0) {
    fuelRateMargin =
      ((sellFuelSurcharge - buyFuelSurcharge) / sellFuelSurcharge) * 100;
  }

  let totalRateMargin = 0;
  if (sellRateTotal > 0) {
    totalRateMargin = ((sellRateTotal - buyRateTotal) / sellRateTotal) * 100;
  }

  return (
    <div className='p-2 min-w-[260px] max-w-[calc(100vw-2rem)]'>
      <table className='text-xs border-collapse table-fixed w-full'>
        <thead>
          <tr>
            <th className='text-left pr-4 pb-2 w-[40%]' />
            <th className='text-left pr-4 pb-2 font-semibold whitespace-nowrap'>
              Buy
            </th>
            <th className='text-left pr-4 pb-2 font-semibold whitespace-nowrap'>
              Sell
            </th>
            <th className='text-left pb-2 font-semibold whitespace-nowrap'>
              Margin
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              className='pr-4 py-1 text-neutral-600 max-w-0 truncate'
              title='Buy Rate'
            >
              Buy Rate
            </td>
            <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
              {formatCurrency(buyRateFreightCharges, 'USD', 2)}
            </td>
            <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
              {formatCurrency(sellRateFreightCharges, 'USD', 2)}
            </td>
            <td className='py-1 text-neutral-600 whitespace-nowrap tabular-nums'>
              ({markupAmount.toFixed(2)}%)
            </td>
          </tr>
          <tr>
            <td
              className='pr-4 py-1 text-neutral-600 max-w-0 truncate'
              title='Fuel Rate'
            >
              Fuel Rate
            </td>
            <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
              {formatCurrency(buyFuelSurcharge, 'USD', 2)}
            </td>
            <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
              {formatCurrency(sellFuelSurcharge, 'USD', 2)}
            </td>
            <td className='py-1 text-neutral-600 whitespace-nowrap tabular-nums'>
              ({fuelRateMargin.toFixed(2)}%)
            </td>
          </tr>

          {(rateMetadata.accessorialPrices as Undef<AccessorialPrice[]>)?.map(
            (accessorial) => (
              <tr key={accessorial.accessorialName}>
                <td
                  className='pr-4 py-1 text-neutral-600 max-w-0 truncate'
                  title={accessorial.accessorialName}
                >
                  {accessorial.accessorialName}
                </td>
                <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
                  {formatCurrency(accessorial.buy, 'USD', 2)}
                </td>
                <td className='pr-4 py-1 whitespace-nowrap tabular-nums'>
                  {formatCurrency(accessorial.sell, 'USD', 2)}
                </td>
                <td className='py-1 text-neutral-600 whitespace-nowrap tabular-nums'>
                  (0.00%)
                </td>
              </tr>
            )
          )}

          <tr className='border-t border-neutral-200'>
            <td
              className='pr-4 py-1 font-semibold max-w-0 truncate'
              title='Total'
            >
              Total
            </td>
            <td className='pr-4 py-1 font-semibold whitespace-nowrap tabular-nums'>
              {formatCurrency(buyRateTotal, 'USD', 2)}
            </td>
            <td className='pr-4 py-1 font-semibold whitespace-nowrap tabular-nums'>
              {formatCurrency(sellRateTotal, 'USD', 2)}
            </td>
            <td className='py-1 font-semibold text-neutral-600 whitespace-nowrap tabular-nums'>
              ({totalRateMargin.toFixed(2)}%)
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
